import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./i18n"; // Initialisiert i18next
import HomePage from "./pages/HomePage";
import TaoluDetailsPage from "./pages/TaoluDetailsPage";
import ChinWooDetailsPage from "./pages/ChinWooDetailsPage";
import SponsorDetailsPage from "./pages/SponsorDetailsPage";
import SponsorsPartnersPage from "./pages/SponsorsPartnersPage";
import PoliciesPage from "./pages/PoliciesPage";
import DownloadsPage from "./pages/DownloadsPage";
import Login from "./components/Login";
import AdminTailwindDashboard from './pages/AdminTailwindDashboard';
import RequireAuth from "./components/auth/RequireAuth";
import AccessDenied from "./components/auth/AccessDenied";
import CategoriesTailwind from './components/admin/CategoriesTailwind';
import GroupsTailwind from './components/admin/GroupsTailwind';
import ScheduleTailwind from './components/admin/ScheduleTailwind';
import SettingsTailwind from './components/admin/SettingsTailwind';
import MedalTableTailwind from './components/admin/MedalTableTailwind';
import MVPTableTailwind from './components/admin/MVPTableTailwind';
import ResultsTailwind from './components/admin/ResultsTailwind';
import UsersTailwind from './components/admin/UsersTailwind';
import UserRolesTailwind from './components/admin/UserRolesTailwind';
import ParticipantsTailwind from './components/admin/ParticipantsTailwind';
import NationsTailwind from './components/admin/NationsTailwind';
import ScorekeeperView from './components/scorekeeper/ScorekeeperView';
import LiveResultsView from './components/scorekeeper/LiveResultsView';
import CallerView from './components/caller/CallerView';
import PublicResults from './components/public/PublicResults';
import PublicMedalTable from './components/public/PublicMedalTable';
import PublicMVPTable from './components/public/PublicMVPTable';
import withTailwindPage from './components/generic/withTailwindPage';
import "./styles/App.css";

const CategoriesTailwindPage = withTailwindPage(CategoriesTailwind);
const GroupsTailwindPage = withTailwindPage(GroupsTailwind);
const ScheduleTailwindPage = withTailwindPage(ScheduleTailwind);
const SettingsTailwindPage = withTailwindPage(SettingsTailwind);
const MedalTableTailwindPage = withTailwindPage(MedalTableTailwind);
const MVPTableTailwindPage = withTailwindPage(MVPTableTailwind);
const ResultsTailwindPage = withTailwindPage(ResultsTailwind);
const UsersTailwindPage = withTailwindPage(UsersTailwind);
const UserRolesTailwindPage = withTailwindPage(UserRolesTailwind);
const ParticipantsTailwindPage = withTailwindPage(ParticipantsTailwind);
const NationsTailwindPage = withTailwindPage(NationsTailwind);

function App() {
  const { i18n } = useTranslation();

  // Sprache aus URL holen und setzen
  const setLanguageFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get("lang") || "de"; // Fallback auf Deutsch
    i18n.changeLanguage(lang); // Sprache setzen
  };

  useEffect(() => {
    setLanguageFromURL();
  }, []);

  return (
    <Router>
      <Routes>
        {/* Route für die Homepage */}
        <Route path="/" element={<HomePage />} />

        {/* Route für die Admin-Seite */}
        <Route path="/admin" element={<Login />} />
        <Route
          path="/admin/dashboard"
          element={
            <RequireAuth roles={['admin', 'scorekeeper', 'caller']}>
              <AdminTailwindDashboard />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <RequireAuth permissions={['manage_categories']}>
              <CategoriesTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/participants"
          element={
            <RequireAuth permissions={['manage_participants']}>
              <ParticipantsTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/nations"
          element={
            <RequireAuth permissions={['manage_nations']}>
              <NationsTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/groups"
          element={
            <RequireAuth permissions={['manage_groups']}>
              <GroupsTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/schedule"
          element={
            <RequireAuth permissions={['manage_schedule']}>
              <ScheduleTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RequireAuth permissions={['manage_schedule_settings', 'manage_data']}>
              <SettingsTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/medal-table"
          element={
            <RequireAuth permissions={['view_results']}>
              <MedalTableTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/mvp-table"
          element={
            <RequireAuth permissions={['view_results']}>
              <MVPTableTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/results"
          element={
            <RequireAuth permissions={['view_results', 'manage_results']}>
              <ResultsTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/scorekeeper"
          element={
            <RequireAuth roles={['scorekeeper']}>
              <ScorekeeperView />
            </RequireAuth>
          }
        />
        <Route
          path="/caller"
          element={
            <RequireAuth permissions={['view_caller']}>
              <CallerView />
            </RequireAuth>
          }
        />
        <Route
          path="/live-results"
          element={<LiveResultsView />}
        />
        <Route
          path="/admin/user-roles"
          element={
            <RequireAuth permissions={['manage_roles']}>
              <UserRolesTailwindPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAuth roles={['superadmin']}>
              <UsersTailwindPage />
            </RequireAuth>
          }
        />

        {/* Route für Zugriff verweigert */}
        <Route path="/access-denied" element={<AccessDenied />} />

        {/* Route für Detailseiten */}
        <Route path="/taolu" element={<TaoluDetailsPage />} />
        <Route path="/chinwoo" element={<ChinWooDetailsPage />} />
        <Route path="/sponsors" element={<SponsorDetailsPage />} />
        <Route path="/sponsors-partners" element={<SponsorsPartnersPage />} />
        <Route path="/policies" element={<PoliciesPage />} />
        <Route path="/downloads" element={<DownloadsPage />} />

        {/* Öffentliche Ergebnisse-Seiten (read-only) */}
        <Route path="/results" element={<PublicResults />} />
        <Route path="/medal-table" element={<PublicMedalTable />} />
        <Route path="/mvp-table" element={<PublicMVPTable />} />
      </Routes>
    </Router>
  );
}

export default App;
